import { ChatModelRunOptions, ChatModelRunResult, ExportedMessageRepository, ExportedMessageRepositoryItem, ReasoningMessagePart, RuntimeAdapterProvider, TextMessagePart, ThreadAssistantMessage, ThreadAssistantMessagePart, ThreadHistoryAdapter, ThreadSystemMessage, ThreadUserMessage, ToolCallMessagePart, useAui, useAuiEvent, useAuiState } from "@assistant-ui/react";
import { useRouteContext } from "../contexts/routeContext";
import { FC, PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import { clearLastEventId, DocumentCitationData, getLastEventId, readJsonSse, safeJsonParse, streamChatToRunResults, ThinkingMetadata, SubAgentLanePart, DocEditCyclePart } from "../chatModelAdapters";
import { useAppEvent } from "@/lib/events";
import { apiAuthFetch } from "@/lib/authFetch";
import { ClientLogger } from "@/lib/clientLogger";
import { onDocumentCitation } from "@/components/assistant";
import { useGeneratedDocumentStore } from "@/components/document/generatedDocumentStore";
import { useReconnectStateStore } from "../stores/reconnectStateStore";
import { ContextWindowSnapshot, useContextBudgetStore } from "../stores/contextBudgetStore";
import { getLastViewedBranch, setLastViewedBranch } from "@/lib/storage";
import { loadThreadMessages } from "./threadHistoryLoad";
import { FRONTEND_INPUT_TOOL_NAMES, SUB_AGENT_TOOL_NAMES, TOOL_TO_DOC_EDIT_VARIANT, DOC_EDIT_PHASE_TOOLS, type DocEditVariant } from "@/lib/toolConstants";
import { parseStepPrefix } from "@/lib/stepParsing";
import { StreamVisibilityManager, isVisibilitySignal } from "@/lib/streamVisibilityManager";
import {
  BASE_DELAY_MS,
  HttpNonRetryableError,
  HttpRetryableError,
  MAX_RETRY_ATTEMPTS,
  MaxRetriesExceededError,
  decorrelatedJitter,
  isRetryableError,
  isRetryableHttpStatus,
} from "@/lib/retryPolicy";

// ── Module-level circuit-breaker for the history-restored → remount → resume loop ──
// When `restored` fires, we add the threadId here BEFORE remounting. The fresh load()
// after remount checks this set and suppresses `unstable_resume`, breaking the infinite
// cycle: restored → remountKey++ → load(unstable_resume:true) → resume → restored …
// The entry is consumed (deleted) inside load() after checking — no external cleanup needed.
// A cleanup effect previously defeated the breaker by deleting the entry during unmount
// (which fires between the add and the fresh load on remount — React key-change semantics).
const suppressResumeAfterRestore = new Set<string>();

// Pause until the browser regains network connectivity, or throw on abort.
// We avoid burning a retry budget while obviously offline — once `online`
// fires, the retry generator picks up where it left off.
async function waitForOnlineOrAbort(signal: AbortSignal): Promise<void> {
  if (typeof window === "undefined") return;
  if (navigator.onLine) return;
  await new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const cleanup = () => {
      window.removeEventListener("online", onOnline);
      signal.removeEventListener("abort", onAbort);
    };
    const onOnline = () => { cleanup(); resolve(); };
    const onAbort = () => { cleanup(); reject(new DOMException("Aborted", "AbortError")); };
    window.addEventListener("online", onOnline, { once: true });
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

// Backoff sleep that short-circuits on `visibilitychange → visible` (a foregrounded
// tab probably wants to retry now) and on user abort. Deliberately NOT short-circuited
// by the `online` event: `online` reports route-level connectivity (NIC up, WiFi
// associated), NOT server reachability. On blocked networks (corporate WiFi without
// egress, captive portals) `online` fires repeatedly while the actual fetch keeps
// failing — letting it bypass the backoff drains the entire retry budget in seconds.
// The exponential backoff is the throttle; we wait it out. See incident 2026-05-14.
export async function delayWithShortCircuits(delayMs: number, signal: AbortSignal): Promise<void> {
  if (typeof window === "undefined") {
    await new Promise(r => setTimeout(r, delayMs));
    return;
  }
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const cleanup = () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      signal.removeEventListener("abort", onAbort);
    };
    const timer = setTimeout(() => { cleanup(); resolve(); }, delayMs);
    const onVisibility = () => { if (!document.hidden) { cleanup(); resolve(); } };
    const onAbort = () => { cleanup(); reject(new DOMException("Aborted", "AbortError")); };
    document.addEventListener("visibilitychange", onVisibility);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

export function createResumeStream(remoteId: string) {
  return async function* (options: ChatModelRunOptions): AsyncGenerator<ChatModelRunResult, void, unknown> {
    const { abortSignal, unstable_assistantMessageId } = options;
    const reconnectStore = useReconnectStateStore.getState();

    // Combine the library's abort signal with our visibility-scoped signal.
    // When the user switches threads, the visibility signal fires and the
    // stream aborts gracefully (no retry, no error banner).
    const visibilitySignal = StreamVisibilityManager.getSignal(remoteId);
    const combinedSignal = AbortSignal.any([abortSignal, visibilitySignal]);

    // A fresh invocation is the expected "connecting" phase before the first
    // resumed SSE frame arrives. Keep this distinct from retry-after-failure.
    reconnectStore.setConnecting(remoteId);

    let prevDelay = BASE_DELAY_MS;

    for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
      if (combinedSignal.aborted) {
        // Visibility abort: user navigated to a different thread. Throw AbortError
        // so assistant-ui marks the message as "incomplete/cancelled" instead of
        // "complete". The remountKey mechanism in ThreadHistoryProvider will trigger
        // a fresh load() + resume when the user navigates back.
        if (visibilitySignal.aborted && isVisibilitySignal(visibilitySignal)) {
          reconnectStore.setIdle(remoteId);
          throw new DOMException("Stream interrupted: thread not visible", "AbortError");
        }
        throw new DOMException("Aborted", "AbortError");
      }

      // Pause while the browser reports offline; resume on `online` or abort.
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        await waitForOnlineOrAbort(combinedSignal);
      }

      try {
        // Cursor: backend tagged each SSE frame with `id:` during the original
        // stream; we stored the latest per-thread in sessionStorage. Send it
        // back so the server skips frames we've already rendered — without it
        // the user sees tool cards flash through their interactive state on
        // reconnect, and on retry we'd duplicate any chunks yielded so far.
        const lastEventId = getLastEventId(remoteId);
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        if (lastEventId) headers["Last-Event-ID"] = lastEventId;

        const res = await apiAuthFetch(`api/chat/resume`, {
          method: "POST",
          headers,
          signal: combinedSignal,
          body: JSON.stringify({
            threadId: remoteId,
            mode: "resume",
            assistantMessageLocalId: unstable_assistantMessageId,
          }),
        });

        if (!res.ok) {
          const message = `Resume failed: ${res.status} ${res.statusText}`;
          if (isRetryableHttpStatus(res.status)) {
            throw new HttpRetryableError(res.status, message);
          }
          throw new HttpNonRetryableError(res.status, message);
        }

        // Success path. yield* delegates the stream chunks to assistant-ui;
        // when the SSE source ends (FinishEvent) the generator returns
        // normally and we exit the retry loop.
        let yieldedFirstResult = false;
        for await (const result of streamChatToRunResults(
          res,
          combinedSignal,
          { readJsonSse, safeJsonParse },
          onDocumentCitation,
          remoteId,
        )) {
          if (!yieldedFirstResult) {
            yieldedFirstResult = true;
            reconnectStore.setIdle(remoteId);
          }
          yield result;
        }
        reconnectStore.setIdle(remoteId);
        return;
      } catch (err) {
        // Visibility abort: user navigated away from this thread. Throw AbortError
        // so assistant-ui marks the message as "incomplete/cancelled". The
        // remountKey mechanism will trigger a fresh load() + resume on return.
        if (isVisibilityAbort(err, visibilitySignal)) {
          reconnectStore.setIdle(remoteId);
          throw new DOMException("Stream interrupted: thread not visible", "AbortError");
        }
        if (combinedSignal.aborted) {
          reconnectStore.setIdle(remoteId);
          throw err;
        }
        if (!isRetryableError(err)) {
          reconnectStore.setIdle(remoteId);
          throw err;
        }
        if (attempt === MAX_RETRY_ATTEMPTS) {
          reconnectStore.setExhausted(remoteId);
          throw new MaxRetriesExceededError(MAX_RETRY_ATTEMPTS);
        }

        // Publish the *upcoming* attempt number so the banner appears once
        // the first attempt has failed — the gate is `attempt >= 2`.
        const nextAttempt = attempt + 1;
        reconnectStore.setRetrying(remoteId, nextAttempt);

        const delay = decorrelatedJitter(prevDelay);
        prevDelay = delay;

        await delayWithShortCircuits(delay, combinedSignal);
      }
    }
  };
}

/**
 * Determine if an error was caused by a visibility-triggered abort (user
 * switched threads) vs. a network failure or library-initiated abort.
 */
function isVisibilityAbort(err: unknown, visibilitySignal: AbortSignal): boolean {
  if (!(err instanceof DOMException) || err.name !== "AbortError") return false;
  return visibilitySignal.aborted && isVisibilitySignal(visibilitySignal);
}

export type ThreadRole = "user" | "assistant" | "system";

export type ThreadHistoryItemPartType = "text" | "reasoning" | "tool_call";

export interface ThreadHistoryItemPart {
  content: string;
  type: ThreadHistoryItemPartType;
  toolCallId?: string | null;
  arguments?: string | null;      // JSON string for tool args
  functionName?: string | null;   // tool / function name
  mcpServerName?: string | null;
  toolTitle?: string | null;
  lastProgressMessage?: string | null;
}

export interface ThreadHistoryCitation {
  docId: string;
  name?: string;
  pages?: string;
  snippet?: string;
}

export interface ThreadHistoryWebSource {
  url: string;
  title?: string | null;
  citedText?: string | null;
}

// Wire-format mirror of `Domain.AssistantMessageTerminalState`.
// Backend serializes as string; values must stay in lockstep with the C# enum.
export type ThreadHistoryItemTerminalState =
  | "Completed"
  | "Interrupted"
  | "TruncatedByLength"
  | "CancelledByUser"
  | "AwaitingUserInput";

export interface ThreadHistoryItem {
  id: string;
  parentId: string | null;
  role: ThreadRole;
  parts: ThreadHistoryItemPart[];
  createdAt: string; // ISO datetime from C#
  citations?: ThreadHistoryCitation[];
  webSources?: ThreadHistoryWebSource[] | null;
  modelId?: string | null;
  generatedDocumentIds?: string[] | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
  reasoningTokens?: number | null;
  cachedTokens?: number | null;
  totalTokens?: number | null;
  totalDurationMs?: number | null;
  estimatedCostUsd?: number | null;
  estimatedCostDisplay?: string | null;
  // Null for non-assistant messages and for messages from before the terminal-state
  // migration (the migration backfills to "Completed"). Continue button fires on "Interrupted".
  terminalState?: ThreadHistoryItemTerminalState | null;
  // Typed thinking metadata on assistant messages that ran the lightweight
  // thinking phase. Contains the task title for the process-window header.
  metadata?: ThinkingMetadata | null;
}

const baseMetadata = {
  unstable_state: JSON.parse("{}"),
  unstable_annotations: JSON.parse("[]"),
  unstable_data: JSON.parse("[]"),
  steps: JSON.parse("[]"),
  custom: JSON.parse("{}"),
} as const;

function toTextPart(text: string): TextMessagePart {
  return { type: "text", text };
}



// Backend message ids are GUIDs; assistant-ui's `generateId()` produces short base62
// strings during the window before a user/assistant message has been persisted. Skip
// branch-pointer writes for those — the backend would 404 anyway.
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Module-scope so cached-runtime remounts don't re-fire PATCH for a
// (threadId, leafId) pair we've already synced this session. assistant-ui
// keeps a ThreadHistoryProvider mounted per cached runtime, and each provider
// instance's useRef resets on remount — without this, every navigation
// through threads re-runs the PATCH against every previously-visited thread.
const lastSyncedLeafByThread = new Map<string, string>();
// Per (threadId, leafId) pairs the server has rejected with a persistent 4xx.
// Retrying with the same payload is pointless and was the source of the
// 404-storm regression. Cleared only on page reload (Map lives in module
// scope, not survive a reload).
const failedLeafByThread = new Map<string, Set<string>>();

// Widened part types — assistant-ui's official types don't include our custom
// extensions (metadata on reasoning, mcpServerName/toolTitle/state on tool-call).
type MappedReasoningPart = ReasoningMessagePart & { metadata?: ThinkingMetadata; stepLabel?: string; stepIndex?: number };
type MappedToolCallPart = ToolCallMessagePart & {
  argsText?: string;
  mcpServerName?: string;
  toolTitle?: string;
  state?: "partial" | "pending" | "complete";
};
type MappedAssistantPart = TextMessagePart | MappedReasoningPart | MappedToolCallPart | SubAgentLanePart | DocEditCyclePart;

function mapHistoryPartToAssistantPart(
  part: ThreadHistoryItemPart
): MappedAssistantPart {
  switch (part.type.toLowerCase()) {
    case "text":
      return { type: "text", text: part.content };

    case "reasoning": {
      const { stepIndex, stepLabel, bodyText } = parseStepPrefix(part.content);
      const reasoningPart: MappedReasoningPart = {
        type: "reasoning",
        text: bodyText,
        ...(stepLabel && { stepLabel }),
        ...(stepIndex != null && { stepIndex }),
      };
      return reasoningPart;
    }

    case "toolcall": {
      const toolName = part.functionName ?? "tool";
      const isPendingFrontendInput =
        FRONTEND_INPUT_TOOL_NAMES.has(toolName) && (part.content ?? "") === "";
      return {
        type: "tool-call",
        toolCallId: part.toolCallId ?? "",
        toolName,
        args: safeJson(part.arguments, {}),
        argsText: part.arguments ?? "",
        result: part.content,
        mcpServerName: part.mcpServerName ?? undefined,
        toolTitle: part.toolTitle ?? undefined,
        progressMessage: part.lastProgressMessage ?? undefined,
        state: isPendingFrontendInput ? "pending" : "complete",
      };
    }

    default:
      return { type: "text", text: part.content };
  }
}

function safeJson<T>(json: string | null | undefined, fallback: T): T {
  try {
    return json ? (JSON.parse(json) as T) : fallback;
  } catch {
    return fallback;
  }
}

// ─── Sub-Agent Lane Reconstruction ─────────────────────────────────────────────
// During streaming, sub-agent lanes are `{ type: "data", name: "sub-agent-lane" }`
// UiParts created from SSE events. These `data` parts are not persisted — only the
// `research()` / `continue_research()` tool-call parts survive. This function scans
// the content array for those tool calls, parses their args+result JSON, and injects
// synthetic `SubAgentLanePart` data parts so the process window renders identically
// on cold reload as it did during streaming.

type SubAgentResultJson = {
  conversationId?: string;
  status?: string;
  answer?: string;
  summary?: string;
  error?: string;
  toolCalls?: Array<{
    toolCallId: string;
    toolName: string;
    mcpServerName?: string;
    toolTitle?: string;
    status: string;
    durationMs?: number;
    arguments?: string;
    resultPreview?: string;
  }>;
};

type ResearchArgsJson = {
  query?: string;
  locale?: string;
  domain?: string;
};

type ContinueResearchArgsJson = {
  conversationId?: string;
  message?: string;
};

export function reconstructSubAgentLanes(content: MappedAssistantPart[]): MappedAssistantPart[] {
  // First pass: index research() calls by conversationId so continue_research can
  // inherit locale/agentType from its parent.
  const researchByConvId = new Map<string, { locale: string; agentType: string }>();
  for (const part of content) {
    if (part.type !== "tool-call") continue;
    if (part.toolName !== "research") continue;
    const args = safeJson<ResearchArgsJson>(part.argsText, {});
    const result = safeJson<SubAgentResultJson>(typeof part.result === "string" ? part.result : null, {});
    if (result.conversationId && args.locale) {
      researchByConvId.set(result.conversationId, {
        locale: args.locale,
        agentType: `aspi-research-${args.locale}-${args.domain ?? "legislation"}`,
      });
    }
  }

  const enriched: MappedAssistantPart[] = [];

  for (const part of content) {
    if (part.type !== "tool-call" || !SUB_AGENT_TOOL_NAMES.has(part.toolName)) {
      enriched.push(part);
      continue;
    }

    const resultStr = typeof part.result === "string" ? part.result : null;
    const result = safeJson<SubAgentResultJson>(resultStr, {});

    // Skip reconstruction when the tool call was interrupted (no result at all)
    if (!resultStr || !result.conversationId) {
      ClientLogger.debug("Sub-agent lane reconstruction skipped: missing result", {
        component: "threadHistoryProvider",
        metadata: { toolCallId: part.toolCallId, toolName: part.toolName },
      });
      enriched.push(part);
      continue;
    }

    const status: SubAgentLanePart["status"] =
      result.status === "completed" ? "completed"
        : result.status === "failed" || result.status === "cancelled" ? "failed"
          : "completed";

    let query: string;
    let locale: string;
    let agentType: string;

    if (part.toolName === "research") {
      const args = safeJson<ResearchArgsJson>(part.argsText, {});
      query = args.query ?? "";
      locale = args.locale ?? "cs";
      agentType = `aspi-research-${locale}-${args.domain ?? "legislation"}`;
    } else {
      // continue_research — inherit from the parent research() call if possible
      const args = safeJson<ContinueResearchArgsJson>(part.argsText, {});
      query = args.message ?? "";
      const parent = args.conversationId ? researchByConvId.get(args.conversationId) : undefined;
      locale = parent?.locale ?? "cs";
      agentType = parent?.agentType ?? "aspi-research";
    }

    const lane: SubAgentLanePart = {
      type: "data",
      name: "sub-agent-lane",
      runId: part.toolCallId,
      conversationId: result.conversationId,
      agentType,
      query,
      locale,
      status,
      summary: result.summary,
      answer: result.answer,
      error: result.error,
      toolCalls: (result.toolCalls ?? []).map(tc => ({
        toolCallId: tc.toolCallId,
        toolName: tc.toolName,
        mcpServerName: tc.mcpServerName,
        toolTitle: tc.toolTitle,
        status: tc.status === "error" ? "error" as const : "success" as const,
        argsText: tc.arguments,
        result: tc.resultPreview,
      })),
    };

    // Insert lane before the tool call. buildUnifiedTimeline filters the tool call
    // via SUB_AGENT_TOOL_NAMES, so only the lane part produces a visual entry.
    enriched.push(lane);
    enriched.push(part);
  }

  return enriched;
}

// ─── Doc-Edit Cycle Reconstruction ─────────────────────────────────────────────
// During streaming, `doc-edit-cycle` data parts carry documentName, narration, and
// summary from SSE events. These aren't persisted. The stepper phases still render
// correctly from tool-call parts alone (useDocumentEditFlow), but documentName is
// lost. This function reconstructs minimal DocEditCyclePart data parts so the
// stepper header can display the document name on cold reload.
//
// What CAN be reconstructed: variant, documentName (from saving tool's result JSON
// or toolTitle), status. What CANNOT: narration and summary (LLM-generated during
// streaming, not stored in any tool result).

const SAVING_TOOLS = DOC_EDIT_PHASE_TOOLS.saving;

export function reconstructDocEditCycles(content: MappedAssistantPart[]): MappedAssistantPart[] {
  // Quick check: any doc-edit tools at all?
  const hasDocEditTools = content.some(
    p => p.type === "tool-call" && TOOL_TO_DOC_EDIT_VARIANT[p.toolName] != null,
  );
  if (!hasDocEditTools) return content;

  // Identify cycle boundaries by scanning for variant-starting skill tools.
  // Same grouping logic as useDocumentEditFlow.
  type CycleInfo = {
    variant: DocEditVariant;
    tools: MappedToolCallPart[];
    firstToolIdx: number;
  };

  const cycles: CycleInfo[] = [];
  let currentVariant: DocEditVariant | null = null;
  let currentTools: MappedToolCallPart[] = [];
  let firstToolIdx = -1;

  // Collect only doc-edit tool calls in order
  for (let i = 0; i < content.length; i++) {
    const part = content[i];
    if (part.type !== "tool-call") continue;

    const variant = TOOL_TO_DOC_EDIT_VARIANT[part.toolName];
    if (variant) {
      // Skill tool starts a new cycle (or retry if same variant)
      if (currentVariant === variant && currentTools.length > 0) {
        // Same variant = retry — reset current cycle
        currentTools = [part];
        firstToolIdx = i;
      } else {
        // Flush previous cycle
        if (currentVariant && currentTools.length > 0) {
          cycles.push({ variant: currentVariant, tools: currentTools, firstToolIdx });
        }
        currentVariant = variant;
        currentTools = [part];
        firstToolIdx = i;
      }
    } else if (currentVariant && DOC_EDIT_PHASE_TOOLS.editing.has(part.toolName)
      || SAVING_TOOLS.has(part.toolName)
      || DOC_EDIT_PHASE_TOOLS.reviewing.has(part.toolName)) {
      currentTools.push(part);
    }
  }
  // Flush last cycle
  if (currentVariant && currentTools.length > 0) {
    cycles.push({ variant: currentVariant, tools: currentTools, firstToolIdx });
  }

  if (cycles.length === 0) return content;

  // Build DocEditCyclePart for each cycle
  const insertions = new Map<number, DocEditCyclePart>();

  for (let ci = 0; ci < cycles.length; ci++) {
    const cycle = cycles[ci];

    // Extract documentName and editSummary from saving tool's result or toolTitle
    let documentName: string | undefined;
    let editSummary: string | undefined;
    for (const tool of cycle.tools) {
      if (!SAVING_TOOLS.has(tool.toolName)) continue;
      // Try result JSON first (CommitDraftToolResult has DocumentName)
      if (typeof tool.result === "string" && tool.result) {
        const r = safeJson<Record<string, unknown>>(tool.result, {});
        const name = (r.DocumentName ?? r.documentName) as string | undefined;
        if (name && !documentName) { documentName = name; }
        const summary = r.editSummary as string | undefined;
        if (summary && !editSummary) { editSummary = summary; }
        if (documentName && editSummary) break;
      }
      // Fall back to toolTitle (e.g. "Revize: Kupní smlouva")
      if (!documentName && tool.toolTitle) { documentName = tool.toolTitle; }
    }

    const allSavingDone = cycle.tools
      .filter(t => SAVING_TOOLS.has(t.toolName))
      .some(t => t.state === "complete");

    insertions.set(cycle.firstToolIdx, {
      type: "data",
      name: "doc-edit-cycle",
      editId: `cycle-${ci}`,
      variant: cycle.variant,
      documentName,
      summary: editSummary,
      status: allSavingDone ? "complete" : "running",
    });
  }

  // Build enriched content with cycle parts injected before their first tool
  const enriched: MappedAssistantPart[] = [];
  for (let i = 0; i < content.length; i++) {
    const cyclePart = insertions.get(i);
    if (cyclePart) enriched.push(cyclePart);
    enriched.push(content[i]);
  }

  return enriched;
}

export function mapHistoryItemToThreadMessage(
  item: ThreadHistoryItem
): ThreadUserMessage | ThreadAssistantMessage | ThreadSystemMessage {
  const createdAt = new Date(item.createdAt);

  if (item.role.toLowerCase() === "assistant") {
    const citations: DocumentCitationData[] | undefined = item.citations?.map(c => ({
      docId: c.docId,
      name: c.name,
      pages: c.pages,
      snippet: c.snippet,
    }));

    const webSources = item.webSources?.length
      ? item.webSources.map(s => ({
          url: s.url,
          title: s.title ?? null,
          citedText: s.citedText ?? null,
        }))
      : undefined;

    // Deduplicate tool-call parts by toolCallId.
    // A backend race condition (concurrent ReplaySubject subscribers during
    // auto-reconnect / page reload) can persist the same ToolCallMessagePart twice.
    const seenToolCallIds = new Set<string>();
    const content = reconstructDocEditCycles(
      reconstructSubAgentLanes(
        item.parts
          .map(mapHistoryPartToAssistantPart)
          .filter(part => {
            if (part.type === "tool-call") {
              if (seenToolCallIds.has(part.toolCallId)) return false;
              seenToolCallIds.add(part.toolCallId);
            }
            return true;
          }),
      ),
    );

    // Attach the message-level metadata onto the first reasoning part so the
    // process-window.tsx header can display the task title. The wire shape moved
    // this off the part to a per-message audit row backend-side, but the UI
    // continues to treat it as belonging to the reasoning section.
    if (item.metadata) {
      const firstReasoning = content.find(p => p.type === "reasoning") as MappedReasoningPart | undefined;
      if (firstReasoning) {
        firstReasoning.metadata = item.metadata;
      }
    }

    // If the persisted assistant turn ended awaiting user input (a frontend-input
    // tool-call part with empty content survived into history), the message is
    // "complete" from the stream-protocol view but "requires-action" from the UI
    // view — otherwise AskCard short-circuits on status.type === "complete" and
    // the card vanishes on refresh.
    const hasPendingFrontendInput = content.some(
      p => p.type === "tool-call"
        && FRONTEND_INPUT_TOOL_NAMES.has((p as MappedToolCallPart).toolName)
        && ((p as MappedToolCallPart).result ?? "") === "",
    );

    // Continue affordance: an interrupted turn flips message status to `incomplete`
    // so assistant-ui treats it as "not done".
    const isInterrupted = item.terminalState === "Interrupted";

    // Encode terminalState in unstable_data for the Continue button to read.
    const terminalStateData = item.terminalState
      ? [{ type: "terminal-state", state: item.terminalState }]
      : [];

    // Extract Praetor revision references from save_revision tool-call parts for cold-reload.
    // Note: part.type comes from the API as PascalCase ("ToolCall") — use case-insensitive match.
    const praetorRevisions: Array<{ id: string; title?: string }> = [];
    for (const part of item.parts) {
      if (part.type.toLowerCase() === "toolcall" && part.functionName === "save_revision" && part.arguments) {
        try {
          const args = JSON.parse(part.arguments);
          if (args.praetorDocumentId) {
            praetorRevisions.push({
              id: args.praetorDocumentId,
              title: part.toolTitle ?? undefined,
            });
          }
        } catch { /* malformed args — skip */ }
      }
    }

    return {
      id: item.id,
      role: "assistant",
      content,
      createdAt,
      status: hasPendingFrontendInput
        ? { type: "requires-action", reason: "tool-calls" }
        : isInterrupted
          ? { type: "incomplete", reason: "cancelled" }
          : { type: "complete", reason: "stop" },
      metadata: {
        ...baseMetadata,
        unstable_data: [
          ...(citations?.length ? [{ type: "citations", citations }] : []),
          ...(webSources?.length ? [{ type: "web-sources", sources: webSources }] : []),
          ...(item.modelId ? [{ type: "model", modelId: item.modelId }] : []),
          ...(item.totalTokens ? [{
            type: "usage",
            inputTokens: item.inputTokens ?? null,
            outputTokens: item.outputTokens ?? null,
            reasoningTokens: item.reasoningTokens ?? null,
            cachedTokens: item.cachedTokens ?? null,
            totalTokens: item.totalTokens,
            estimatedCostUsd: item.estimatedCostUsd ?? null,
            estimatedCostDisplay: item.estimatedCostDisplay ?? null,
            totalDurationMs: item.totalDurationMs ?? null,
          }] : []),
          ...(item.totalDurationMs != null || item.createdAt
            ? [{ type: "timing", startedAtUtc: item.createdAt, totalDurationMs: item.totalDurationMs ?? null }]
            : []),
          ...(item.generatedDocumentIds?.length
            ? [{ type: "generated-documents", ids: item.generatedDocumentIds }]
            : []),
          ...(praetorRevisions.length
            ? [{ type: "praetor-revisions", revisions: praetorRevisions }]
            : []),
          ...terminalStateData,
        ],
      },
    };
  }

  // user/system: keep it simple => only text parts
  const text = item.parts.map(p => p.content).join("");

  if (item.role.toLowerCase() === "user") {
    return {
      id: item.id,
      role: "user",
      content: [toTextPart(text)],
      attachments: [],
      createdAt,
      metadata: baseMetadata,
    };
  }

  return {
    id: item.id,
    role: "system",
    content: [toTextPart(text)],
    createdAt,
    metadata: baseMetadata,
  };
}

function mapThreadHistoryToExported(
  msgs: ThreadHistoryItem[]
): ExportedMessageRepositoryItem[] {
  return msgs.map((m, i) => ({
    parentId: i === 0 ? null : msgs[i - 1]!.id,
    message: mapHistoryItemToThreadMessage(m),
  }));
}

export class ThreadNotFoundError extends Error {
  constructor(threadId: string) {
    super(`Thread not found: ${threadId}`);
    this.name = "ThreadNotFoundError";
  }
}

// Create a provider component that injects the history adapter
export const ThreadHistoryProvider: FC<PropsWithChildren> = ({ children }) => {
  const threadListItem = useAuiState(({ threadListItem }) => threadListItem);
  const routeContext = useRouteContext();

  const routeContextRef = useRef(routeContext);
  useEffect(() => {
    routeContextRef.current = routeContext;
  }, [routeContext]);

  const aui = useAui();
  const threadId = routeContext.threadId ?? threadListItem?.remoteId;
  const remoteId = threadListItem.remoteId;

  // Active-branch leaf projection. assistant-ui's `thread.messages` is the *active path*
  // through the branch tree, so the last id is the leaf of whatever branch the user is
  // currently viewing — the same hook ContextBudgetDonut subscribes to. Used below to
  // persist branch navigation so a reload (or a different device) restores the same view.
  const activeLeafMessages = useAuiState((s) => s.thread.messages);
  const activeLeafId = activeLeafMessages?.length
    ? activeLeafMessages[activeLeafMessages.length - 1].id
    : undefined;

  // Gate the branch-pointer PATCH on AUI's load state — while the thread is
  // mid-hydration, `thread.messages` may carry a transient leaf from a sibling
  // runtime's state.
  const isLoading = useAuiState((s) => s.thread.isLoading);

  // Incremented to force the RuntimeAdapterProvider's children (including the
  // RuntimeHook → LocalRuntimeCore) to remount. This gives a fresh `_loadPromise`
  // so `__internal_load()` calls `load()` again — the only reliable way to reload
  // thread state because assistant-ui caches `_loadPromise` indefinitely.
  const [remountKey, setRemountKey] = useState(0);

  // Detect when this thread becomes the active thread again after being
  // visibility-aborted (user navigated away while streaming, then returned).
  // Force a remount so the runtime reloads messages and reconnects the stream.
  // Guarded by hasLoadedRef: skip on the first activation of a fresh instance —
  // the initial load()+resume() already handles reconnection. Only fires for
  // cached threads that were previously loaded, navigated away from, then returned to.
  const isActiveThread = routeContext.threadId === remoteId;
  const prevActiveRef = useRef(isActiveThread);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!hasLoadedRef.current) return;
    if (isActiveThread && !prevActiveRef.current) {
      // Thread just became active — check if it was previously streaming (aborted)
      const recentlyActive = StreamVisibilityManager.getRecentlyActiveThreadIds();
      if (remoteId && recentlyActive.includes(remoteId)) {
        StreamVisibilityManager.removeFromRecentlyActive(remoteId);
        useReconnectStateStore.getState().setConnecting(remoteId);
        setRemountKey(k => k + 1);
      }
    }
    prevActiveRef.current = isActiveThread;
  }, [isActiveThread, remoteId]);

  // Listen for backend-generated title pushed via SSE and update sidebar immediately.
  // Scoped by threadId to prevent cross-thread title clobbering during concurrent streams.
  useAppEvent("title-generated", ({ title, threadId: eventThreadId }) => {
    if (title && (!eventThreadId || eventThreadId === remoteId)) {
      aui.threads().item("main").rename(title);
    }
  });

  // When /chat/resume returns a `restored` sentinel, the message is already in DB.
  // Remount the runtime so a fresh load() repopulates the thread from history.
  // Circuit-breaker: suppress the next load's unstable_resume to prevent the loop.
  useAppEvent("history-restored", ({ threadId: eventThreadId }) => {
    if (!eventThreadId || eventThreadId === remoteId) {
      if (remoteId) suppressResumeAfterRestore.add(remoteId);
      setRemountKey(k => k + 1);
    }
  });

  // Continue affordance: the click handler POSTs to Continue, which dispatches a
  // producer on the backend, then emits this event. Remount so the fresh load()
  // sees isResponseCompleted=false → resume() → SSE reconnect.
  useAppEvent("continue-started", ({ threadId: eventThreadId }) => {
    if (eventThreadId === remoteId) {
      setRemountKey(k => k + 1);
    }
  });

  // Self-heal: assistant-ui's import() threw "Parent message not found" — the
  // message tree has a parentId pointing to a message not in the loaded set.
  // Remount to force a fresh load. By the time the retry fires, the server may
  // have cleaned up the stale placeholder that caused the orphan.
  useAppEvent("thread-import-corrupted", () => {
    ClientLogger.error("Thread import corrupted — forcing reload", {
      component: "ThreadHistoryProvider",
      metadata: { threadId: remoteId },
    });
    setRemountKey(k => k + 1);
  });

  // Fallback: if the SSE title event never arrives (backend fire-and-forget failed,
  // or thread loaded from history without a title), call generateTitle() after a delay.
  // The endpoint is idempotent — returns the existing title immediately if already set.
  const title = useAuiState(({ threadListItem }) => threadListItem?.title);
  const titleFallbackAttempted = useRef<string | null>(null);

  useEffect(() => {
    if (!remoteId || title || titleFallbackAttempted.current === remoteId) return;
    const timer = setTimeout(() => {
      titleFallbackAttempted.current = remoteId;
      aui.threads().item("main").generateTitle();
    }, 3000);
    return () => clearTimeout(timer);
  }, [title, remoteId, aui]);

  // Branch-pointer sync. When the user picks a different branch (via the picker, an
  // edit-prompt, or a regenerate), `activeLeafId` flips and we persist it server-side
  // so a reload or another device restores the same branch. Debounced 500ms so rapid
  // clicks through the picker only fire one PATCH. The guard skips:
  //   • non-GUID local ids — runtime generates these before a user message is persisted;
  //     the backend wouldn't find them anyway.
  //   • repeats of the most recently synced id (module-scope, survives remounts).
  //   • activity while AUI is still loading the thread — its messages array may carry
  //     a stale leaf from a previous runtime's state mid-hydration.
  //   • pairs the server has already rejected with 4xx this session.
  useEffect(() => {
    if (!remoteId || !activeLeafId) return;
    if (!GUID_PATTERN.test(activeLeafId)) return;
    if (lastSyncedLeafByThread.get(remoteId) === activeLeafId) return;
    if (isLoading) return;
    if (failedLeafByThread.get(remoteId)?.has(activeLeafId)) return;

    const timer = setTimeout(() => {
      // Mark synced before the fetch — same "best-effort, don't hammer" semantic
      // the original ref had. The 4xx branch below records the pair separately in
      // failedLeafByThread so a persistent reject is blocked even though the
      // lastSynced map says it was attempted.
      lastSyncedLeafByThread.set(remoteId, activeLeafId);
      setLastViewedBranch(remoteId, activeLeafId);

      // Best-effort cross-device sync. localStorage already restores this device on
      // reload; a network failure here only loses cross-device propagation, which the
      // next successful PATCH (or another device's PATCH) will repair.
      void apiAuthFetch(`api/threads/${remoteId}/last-viewed`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leafMessageId: activeLeafId }),
      })
        .then((res) => {
          if (res.ok) return;
          if (res.status === 404 || res.status === 400) {
            // Persistent: leaf is gone or payload is invalid. Block re-fires of
            // this exact pair and surface at WARN — silent debug-only floods are
            // what made this regression invisible across several PRs.
            let set = failedLeafByThread.get(remoteId);
            if (!set) {
              set = new Set();
              failedLeafByThread.set(remoteId, set);
            }
            set.add(activeLeafId);
            // Also evict the stale localStorage entry so the next page load
            // doesn't re-fire the PATCH for a deleted or inaccessible thread.
            // The module-scope failedLeafByThread only survives the session;
            // without this the 404 storm repeats on every reload.
            setLastViewedBranch(remoteId, null);
            ClientLogger.warn("Branch pointer rejected by server", {
              component: "ThreadHistoryProvider",
              action: "PATCH /last-viewed",
              metadata: { status: res.status, threadId: remoteId, leafId: activeLeafId },
            });
            return;
          }
          // 5xx / other transient: don't add to failed set. The next legitimate
          // leaf change will retry naturally; localStorage + server reconcile
          // handle cross-device fallback.
          ClientLogger.debug("Branch pointer persist transient failure", {
            component: "ThreadHistoryProvider",
            action: "PATCH /last-viewed",
            metadata: { status: res.status, threadId: remoteId, leafId: activeLeafId },
          });
        })
        .catch((e) => {
          // Network error / abort — same transient bucket. 401/403 are handled
          // upstream by apiAuthFetch (redirect / forbidden-action event).
          ClientLogger.debug("Branch pointer persist failed", {
            error: e, action: "PATCH /last-viewed", component: "ThreadHistoryProvider",
          });
        });
    }, 500);

    return () => clearTimeout(timer);
  }, [remoteId, activeLeafId, isLoading]);

  const history: ThreadHistoryAdapter = useMemo(
    () => ({
      async append({})
      {
        // no-op since we do that on the backend
      },
      async load(): Promise<ExportedMessageRepository & { unstable_resume?: boolean }> {
          if (!remoteId)
            return { messages: [] };

          const result = await loadThreadMessages(remoteId, {
            apiAuthFetch,
            clearLastEventId,
            getLastViewedBranch,
            setLastViewedBranch,
            setContextWindow: (id, snapshot) => useContextBudgetStore.getState().set(id, snapshot),
            fetchGeneratedDocuments: (ids) => useGeneratedDocumentStore.getState().fetchDocuments(ids),
            clearLoadingFlag: () => {
              const ctx = routeContextRef.current;
              ctx?.onRouteStateChanged({ ...ctx, loadingThreadFromUrl: false });
            },
          });

          if (result.unstable_resume === true && routeContextRef.current.threadId === remoteId) {
            useReconnectStateStore.getState().setConnecting(remoteId);
          }

          hasLoadedRef.current = true;

          // Circuit-breaker: if this load was triggered by a history-restored remount,
          // suppress resume to break the infinite loop (restored → remount → resume → restored…)
          if (suppressResumeAfterRestore.has(remoteId)) {
            suppressResumeAfterRestore.delete(remoteId);
            return { ...result, unstable_resume: false };
          }

          return result;
        },
      async *resume(options: ChatModelRunOptions): AsyncGenerator<ChatModelRunResult, void, unknown> {
        if (!remoteId) throw new Error("Cannot resume: no remote thread ID");
        yield* createResumeStream(remoteId)(options);
      },
    }),
    [remoteId, threadId]
  );

  const adapters = useMemo(() => ({ history }), [history]);

  return (
    <RuntimeAdapterProvider key={remountKey} adapters={adapters}>
      {children}
    </RuntimeAdapterProvider>
  );
};